import { CalendarDays, Pencil, ArrowRight, User } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { cn } from '@/shared/utils/utils';
import type { Project } from '../types';
import {
  DEPT_LABELS,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  deadlineInfo,
  progressPct,
} from '../constants';

interface ProjectCardProps {
  project: Project;
  canEdit: boolean;
  onOpen: (project: Project) => void;
  onEdit: (project: Project) => void;
  onViewTasks: (project: Project) => void;
}

export function ProjectCard({ project, canEdit, onOpen, onEdit, onViewTasks }: ProjectCardProps) {
  const pct = progressPct(project);
  const deadline = deadlineInfo(project.deadline);
  const assigneeName = project.assignee_name ?? project.assignees?.[0]?.display_name;

  return (
    <div
      className="group flex cursor-pointer flex-col gap-3 rounded-card border border-outline bg-surface p-4 shadow-surface-1 transition-all hover:border-primary/30 hover:shadow-surface-2"
      onClick={() => onOpen(project)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-body-md font-bold text-on-surface">{project.name}</h3>
          <span className="text-[10px] uppercase tracking-wide text-on-surface-variant">
            {DEPT_LABELS[project.department] ?? project.department}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge tone={PRIORITY_TONE[project.priority] ?? 'neutral'}>{project.priority}</Badge>
          {canEdit && (
            <button
              type="button"
              aria-label="Edit project"
              className="text-on-surface-variant opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      {project.description && (
        <p className="line-clamp-2 text-body-sm text-on-surface-variant">{project.description}</p>
      )}

      {/* Progress — read-only, derived from backend counts */}
      <div>
        <div className="mb-1 flex justify-between text-[10px] text-on-surface-variant">
          <span>
            {project.done_count ?? 0}/{project.task_count ?? 0} tasks
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-highest">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Assignee */}
      {assigneeName && (
        <div className="flex items-center gap-1.5 text-[11px] text-on-surface-variant">
          <User className="h-3 w-3" />
          <span className="truncate">{assigneeName}</span>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto flex items-center justify-between border-t border-outline pt-2">
        <Badge tone={STATUS_TONE[project.status] ?? 'neutral'}>{STATUS_LABEL[project.status]}</Badge>
        <div className="flex items-center gap-3">
          {deadline && (
            <span
              className={cn(
                'flex items-center gap-1 text-[10px]',
                deadline.urgent ? 'text-danger' : 'text-on-surface-variant',
              )}
            >
              <CalendarDays className="h-3 w-3" />
              {deadline.label}
            </span>
          )}
          <button
            type="button"
            className="flex items-center gap-0.5 text-[11px] font-semibold text-primary hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onViewTasks(project);
            }}
          >
            Tasks <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
